import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useRecoilValue } from 'recoil';
import { accessTokenState } from 'states/Token';

// Initial state - never null
const initialAiEvaluationState = {
  isEnabled: false,
  settings: null
};

// Create context for application-wide settings
const SettingsContext = createContext({
  aiEvaluation: initialAiEvaluationState,
  loading: true,
  error: null,
  refreshSettings: () => {}
});

// Provider component
export const SettingsProvider = ({ children }) => {
  const [aiEvaluation, setAiEvaluation] = useState(initialAiEvaluationState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const accessToken = useRecoilValue(accessTokenState);

  const fetchSettings = async () => {
    console.log('fetchSettings called');
    try {
      setLoading(true);
      setError(null);

      console.log('Calling API: /ai_evaluation/settings');
      // Fetch AI evaluation settings from API (axios automatically handles auth via cookies)
      const response = await axios.get('/ai_evaluation/settings');
      console.log('API call completed');

      if (response.data) {
        console.log('Settings API response:', response.data);
        console.log('is_feature_enabled value:', response.data.is_feature_enabled);
        setAiEvaluation({
          isEnabled: response.data.is_feature_enabled || false,
          settings: response.data
        });
        console.log('aiEvaluation state set to:', {
          isEnabled: response.data.is_feature_enabled || false,
          settings: response.data
        });
      } else {
        setAiEvaluation(initialAiEvaluationState);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      // On error (404, 401, etc.), disable all features
      setAiEvaluation(initialAiEvaluationState);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch settings only after accessToken is loaded
    console.log('accessToken changed:', accessToken);
    if (accessToken !== undefined) {
      fetchSettings();
    }
  }, [accessToken]);

  const value = {
    aiEvaluation,
    loading,
    error,
    refreshSettings: fetchSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook for using the context
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export default SettingsContext;
